import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { observer } from 'mobx-react-lite';
import { useTheme } from 'styled-components';
import { useMeasuredHeight } from './use-measured-height';
import { usePortfolioAssetsCurrencyMode } from './use-portfolio-assets-currency-mode';
import { PortfolioMobxDto } from '../../../../../../models/mobx/dtos/portfolio/portfolio-mobx-dto';
import { AssetPortfolioSimple } from '../asset-portfolio-simple';
import { useRootStore } from '../../../../../../providers/root-store-provider';
import { AssetMiniPersonal } from '../../../mini/personal/asset-mini-personal';
import { AssetMiniStocks } from '../../../mini/stocks/asset-mini-stocks';
import { AssetMiniInvestown } from '../../../mini/investown/asset-mini-investown';
import { IconArrowDown } from '../../../../icons/arrow-down';
import { getOpacity } from '../../../../../../theme/utils/get-rgb';

type AssetPortfolioSimpleRegularType = {
    data: PortfolioMobxDto;
    disabled?: boolean;
    onClick?: () => void;
    showAllAssets?: boolean;
};

export const AssetPortfolioSimpleRegular: React.FC<AssetPortfolioSimpleRegularType> = observer(
    ({ data, disabled = false, onClick, showAllAssets = false }: AssetPortfolioSimpleRegularType) => {
        const theme = useTheme();
        const { portfolioStore } = useRootStore();
        const [isExpanded, setIsExpanded] = useState(false);

        const personalAssets = data.personalAssets || [];
        const portfolioCurrencyCode = data.portfolioHistoryCurrencyCode || data.currency.code;
        const hasAssets = personalAssets.length > 0 || data.numberOfInvestownAssets > 0;
        const { showConvertedValues, currencyRatesBySymbol, toggleValueDisplay } = usePortfolioAssetsCurrencyMode({
            personalAssets,
            portfolioCurrencyCode,
            isExpanded,
            hasInvestown: data.numberOfInvestownAssets > 0,
        });

        const { ref: assetsListRef, height: listHeight } = useMeasuredHeight(showAllAssets && hasAssets, [
            personalAssets.length,
            data.numberOfInvestownAssets,
            showConvertedValues ? 1 : 0,
        ]);

        const handleToggle = (e: React.MouseEvent) => {
            e.stopPropagation();
            setIsExpanded((value) => !value);
        };

        const isActive = data.id === portfolioStore.selectedPortfolio.id;

        return (
            <>
                <div
                    className={`relative z-[1] flex items-center justify-between gap-[1rem] rounded-[99rem] border p-[0.8rem] pr-medium ${
                        isActive
                            ? 'cursor-default border-transparent'
                            : 'cursor-pointer border-border hover:border-[var(--portfolio-hover-border)]'
                    }`}
                    style={{
                        backgroundColor: isActive
                            ? theme.assetColor[data.colorName].activeBackground
                            : theme.card.variant.default.background,
                        color: isActive ? theme.assetColor[data.colorName].text : theme.palette.color.secondary.main,
                        ['--portfolio-hover-border' as string]: getOpacity(theme.palette.color.secondary.main, 0.2),
                    }}
                    onClick={onClick}
                >
                    <AssetPortfolioSimple disabled={disabled} data={data} />
                    {showAllAssets && hasAssets && (
                        <button
                            type="button"
                            onClick={handleToggle}
                            className={`absolute right-small top-1/2 flex -translate-y-1/2 items-center justify-center bg-transparent p-xsmall text-inherit opacity-60 transition-[opacity,transform] duration-200 hover:opacity-100 [&_svg]:h-[1.2rem] [&_svg]:w-[1.2rem] [&_svg]:transition-transform [&_svg]:duration-200 ${
                                isExpanded ? '[&_svg]:rotate-180' : '[&_svg]:rotate-0'
                            }`}
                        >
                            <IconArrowDown />
                        </button>
                    )}
                </div>
                {showAllAssets && hasAssets && (
                    <motion.div
                        ref={assetsListRef}
                        className="relative z-0 -mt-[3rem] overflow-hidden rounded-b-[3rem] border border-border border-t-0 pt-[3rem] [will-change:height,opacity]"
                        style={{ backgroundColor: theme.card.variant.default.background }}
                        initial={false}
                        animate={{
                            height: isExpanded ? listHeight : 0,
                            opacity: isExpanded ? 1 : 0,
                        }}
                        transition={{
                            height: { duration: 0.22, ease: 'easeOut' },
                            opacity: { duration: 0.16, ease: 'easeOut' },
                        }}
                    >
                        <div
                            className="flex flex-col gap-xsmall overflow-hidden p-vsmall"
                            style={{ color: theme.palette.color.secondary.main }}
                        >
                            <AssetMiniStocks data={data} />
                            {personalAssets.map((asset) => (
                                <AssetMiniPersonal
                                    key={asset.id}
                                    data={asset}
                                    portfolioCurrencyCode={portfolioCurrencyCode}
                                    showConvertedValue={showConvertedValues}
                                    currencyRatesBySymbol={currencyRatesBySymbol}
                                    onToggleValueDisplay={toggleValueDisplay}
                                />
                            ))}
                            <AssetMiniInvestown
                                investownTotalValue={data.investownTotalValue}
                                investownTotalValueSource={data.investownTotalValueSource}
                                numberOfInvestownAssets={data.numberOfInvestownAssets}
                                currencyCode={portfolioCurrencyCode}
                                showConvertedValue={showConvertedValues}
                                onToggleValueDisplay={toggleValueDisplay}
                            />
                        </div>
                    </motion.div>
                )}
            </>
        );
    },
);
